import { useState, useEffect, useMemo } from "react";
import HttpApis from "./../services/HttpApis";
import { message } from "antd";
import { trackWatchlistAdd, trackWatchlistRemove } from "./../services/Analytics";

export const useMyList = (authtoken, slug) => {
  const [myList, setMyList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchMyList = async () => {
    if (!authtoken) {
      setMyList([]);
      return;
    }

    try {
      setIsLoading(true);
      const headers = {
        Authorization: `Bearer ${authtoken}`,
        "Content-Type": "application/json",
      };
      const limit = 1000;
      const response = await HttpApis.getList(limit, headers);
      const list = response?.data?.data || [];
      
      // Flatten the list if it's nested
      const flatList = list.flatMap((item) => item.content || item);
      setMyList(flatList);
    } catch (error) {
      setMyList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!authtoken) {
      message.error("Please login");
      return;
    }
    const isAlreadyInList = myList.some((item) => item.slug === slug);
    if (isAlreadyInList) {
      message.info("Item is already in your list");
      return;
    }

    const optimisticItem = { slug, title: "Loading..." };
    setMyList(prev => [...prev, optimisticItem]);

    const headers = {
      Authorization: `Bearer ${authtoken}`,
      "Content-Type": "application/json",
    };

    try {
      const response = await HttpApis.addToList(slug, headers);
      if (response.status >= 200 && response.status < 300) {
        message.success("Added to My List");
        await trackWatchlistAdd(slug);       
        await fetchMyList();
      } else {
        setMyList(prev => prev.filter(item => item.slug !== slug));
        message.error("Failed to add to My List");
      }
    } catch (error) {
      setMyList(prev => prev.filter(item => item.slug !== slug));
      message.error("Failed to add to My List");
    }
  };

  const handleRemove = async () => {
    if (!authtoken) {
      message.error("Please login");
      return;
    }

    const previousList = [...myList];
    setMyList(prev => prev.filter(item => item.slug !== slug));

    const headers = {
      Authorization: `Bearer ${authtoken}`,
      "Content-Type": "application/json",
    };

    try {
      const response = await HttpApis.deleteToList(slug, headers);
      if (response.status >= 200 && response.status < 300) {
        message.success("Removed from My List");
        await trackWatchlistRemove(slug);       
        await fetchMyList();
      } else {
        setMyList(previousList);
        message.error("Failed to remove from My List");
      }
    } catch (error) {
      setMyList(previousList);
      message.error("Failed to remove from My List");
    }
  };

  const isInMyList = useMemo(() => {
    const result = myList.some((item) => item.slug === slug);
    return result;
  }, [myList, slug]);

  useEffect(() => {
    if (authtoken) {
      fetchMyList();
    } else {
      setMyList([]);
    }
  }, [authtoken]);

  return { myList, handleAdd, handleRemove, isInMyList, fetchMyList, isLoading };
};